import { type FormEvent, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CalendarDays, Check } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { B2BHeroDashboardShowcase } from './B2BHeroDashboardShowcase';

/** Hero band — anchored on brand primary #8E86FF */
const HERO_GRADIENT =
  'linear-gradient(160deg, #5c54d8 0%, #6e66e8 28%, #8E86FF 52%, #9b94ff 72%, #c8c4ff 100%)';

const TRUST_CHECKS = [
  'Guided workspace setup',
  '14-day trial',
  'No credit card required',
];

export function B2BHero() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');

  const handleEmailSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    navigate(trimmed ? `/business/demo?email=${encodeURIComponent(trimmed)}` : '/business/demo');
  };

  return (
    <div className="relative">
      {/* ── Gradient hero card ─────────────────────────────────────────────── */}
      <section
        id="hero"
        className="relative mx-2 mt-2 overflow-visible rounded-2xl sm:mx-4 sm:rounded-3xl lg:mx-6 lg:rounded-[2rem]"
        style={{ background: HERO_GRADIENT, paddingBottom: '14rem' }}
        aria-label="GoGrabJob Business hero"
      >
        {/* Decorative blurs */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]" aria-hidden>
          <div
            className="absolute -left-[25%] -top-[20%] h-[85%] w-[75%] rounded-full opacity-[0.15] blur-3xl"
            style={{ background: '#4a42a8' }}
          />
          <div
            className="absolute -right-[10%] top-[30%] h-[60%] w-[55%] rounded-full opacity-[0.18] blur-3xl"
            style={{ background: '#6B63D4' }}
          />
          <svg
            className="absolute bottom-0 left-0 h-28 w-full text-white/[0.09]"
            viewBox="0 0 1440 112"
            preserveAspectRatio="none"
            aria-hidden
          >
            <path fill="currentColor" d="M0,80 C480,112 960,16 1440,48 L1440,112 L0,112 Z" />
          </svg>
        </div>

        {/* ── Centered hero copy ─────────────────────────────────────────────── */}
        <div className="relative z-10 mx-auto max-w-2xl px-6 pt-[max(5.25rem,calc(4.75rem+env(safe-area-inset-top)))] text-center md:pt-16 lg:pt-20">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.06 }}
            className="mb-4 sm:mb-5"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm sm:gap-3 sm:px-4 sm:py-2 sm:text-sm">
              <span>GoGrabJob Business</span>
              <span className="text-white/50" aria-hidden>
                <svg width="4" height="4" viewBox="0 0 4 4" fill="none">
                  <circle cx="2" cy="2" r="2" fill="currentColor" />
                </svg>
              </span>
              <span>ATS · AI interviews · RBAC</span>
            </div>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-4 text-3xl font-extrabold leading-[1.15] tracking-tight text-white sm:text-4xl md:text-[3rem] md:leading-[1.12]"
            style={{ fontWeight: 800 }}
          >
            Reduce time-to-hire.
            <br />
            <span className="text-white/80">Improve quality of hire.</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.16 }}
            className="mx-auto mb-7 max-w-xl text-base leading-relaxed text-white/88 md:text-lg"
          >
            AI interviews, structured workflows, and role-fit scoring — one platform for agencies and in-house teams.
          </motion.p>

          {/* ── Email form + Book demo button ──────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-5 flex flex-col items-center gap-3 px-2 sm:flex-row sm:justify-center"
          >
            <form
              onSubmit={handleEmailSubmit}
              className="flex w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-[0_4px_32px_rgba(0,0,0,0.22)] sm:w-auto"
            >
              <input
                type="email"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your work email"
                className="w-full bg-transparent px-5 py-3.5 text-[15px] text-gray-900 placeholder:text-gray-400 focus:outline-none sm:w-52 md:w-64"
              />
              <button
                type="submit"
                className="m-1.5 shrink-0 rounded-xl bg-gray-900 px-5 py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-gray-700"
              >
                Start now
              </button>
            </form>

            <Link
              to="/business/demo"
              className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-white/15 px-5 py-[0.82rem] text-[14px] font-semibold text-white ring-1 ring-white/25 backdrop-blur-sm transition-colors hover:bg-white/25"
            >
              <CalendarDays className="h-4 w-4 shrink-0" aria-hidden />
              Book a demo
              <ArrowRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
            </Link>
          </motion.div>

          {/* Trust badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.45, delay: 0.26 }}
            className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-white/82"
          >
            {TRUST_CHECKS.map((t) => (
              <div key={t} className="flex items-center gap-1.5">
                <Check className="h-4 w-4 shrink-0" strokeWidth={2.5} aria-hidden />
                {t}
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Product showcase — overlaps the gradient card ──────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.3 }}
        className="relative z-20 mx-auto -mt-[13rem] w-[calc(100%-1.5rem)] max-w-5xl select-none sm:w-[calc(100%-3rem)] md:w-[calc(100%-4rem)] lg:w-[calc(100%-6rem)]"
      >
        <div className="overflow-hidden rounded-2xl shadow-2xl md:rounded-[1.75rem]">
          <B2BHeroDashboardShowcase
            heightClass="h-[min(72vh,540px)] sm:h-[min(76vh,600px)] md:h-[min(78vh,660px)] lg:h-[min(80vh,720px)]"
          />
        </div>
      </motion.div>
    </div>
  );
}
